import { mainStore } from '~/stores';

export type StatusBarColor = 'light-content' | 'dark-content';

interface StatusBarStackItem {
  id: string;
  color: StatusBarColor;
}

/**
 * Global manager for React Native status bar color control
 * Implements stack-based system where the last added color has priority
 * When a component unmounts, the previous color from stack is restored
 */
class StatusBarManager {
  private static instance: StatusBarManager;
  private stack: StatusBarStackItem[] = [];

  private constructor() {}

  static getInstance(): StatusBarManager {
    if (!StatusBarManager.instance) {
      StatusBarManager.instance = new StatusBarManager();
    }
    return StatusBarManager.instance;
  }

  /**
   * Push new status bar color to the stack and apply it
   * @param id Unique identifier for the component
   * @param color Status bar color to set
   */
  push(id: string, color: StatusBarColor): void {
    // Remove existing entry if present to avoid duplicates
    // (can happen in React Strict Mode during development)
    const existingIndex = this.stack.findIndex((item) => item.id === id);
    if (existingIndex !== -1) {
      this.stack.splice(existingIndex, 1);
    }

    this.stack.push({ id, color });
    this.applyCurrentColor();
  }

  /**
   * Remove status bar color from stack by component ID
   * Restores previous color from stack
   * @param id Unique identifier for the component
   */
  pop(id: string): void {
    this.stack = this.stack.filter((item) => item.id !== id);
    this.applyCurrentColor();
  }

  /**
   * Update status bar color for existing component in stack
   * Only applies if this component is currently active (last in stack)
   * @param id Unique identifier for the component
   * @param color New status bar color
   */
  update(id: string, color: StatusBarColor): void {
    const item = this.stack.find((item) => item.id === id);
    if (item && item.color !== color) {
      item.color = color;
      // Only update if this is the currently active element
      if (this.stack[this.stack.length - 1]?.id === id) {
        this.applyCurrentColor();
      }
    }
  }

  /**
   * Apply current color from the top of stack to React Native
   * Falls back to 'light-content' if stack is empty
   */
  private applyCurrentColor(): void {
    const current = this.stack[this.stack.length - 1];
    const color = current?.color || 'light-content';

    try {
      mainStore.sendToRN('setStatusBar', color);
    } catch (error) {
      console.error('Failed to set status bar color:', error);
    }
  }
}

export default StatusBarManager;
